import axios, { AxiosError } from 'axios'
import React, { FunctionComponent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'
import { $enum } from 'ts-enum-util'

import { DiscontinueModal } from './DiscontinueModal/DiscontinueModal'
import { DissociateModal } from './DissociateModal'
import { PhysicianDetails } from './PhysicianDetails'
import {
  getSortColumnFromURLParams,
  ProgramColumn,
  programColumns,
  resolveDiscontinueAvailability
} from './PhysicianDetails.models'
import { TransferOrDiscontinueModal } from './TransferOrDiscontinueModal'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { useUpdateQueryParams } from '../../components/ClinPagination/ClinPagination.model'
import { SortDirectionType } from '../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import { useAppContext } from '../../context/app'
import { createAnnounceEvent } from '../../events/AnnounceEvent'
import { AssociatedPatientsContainer } from '../../features/AssociatedPatients'
import { AssociatedPatientsStatus } from '../../features/AssociatedPatients/AssociatedPatients.model'
import { useEffectOnlyOnce } from '../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsEvent } from '../../services/Analytics'
import { AnalyticsPageEvent } from '../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  cancelGetPhysicianById,
  disassociatePhysician,
  discontinuePhysician,
  getPatients,
  getPhysicianById
} from '../../services/ApiService'
import { IRestEndpointState } from '../../types/IRestEndpointState'
import {
  FacetDto,
  InstituteDto,
  PhysicianDetailRequestDto,
  PhysicianDetailsDto
} from '../../types/swaggerTypes'
import { getDependencyErrorMessage } from '../../utils/getDependencyErrorMessage'
import { useErrorMessage } from '../../utils/useErrorMessage'
import { sortOptions } from '../Patients/Patients.model'

export enum ModalMode {
  active,
  submitting,
  hasError
}

export const PHYSICIAN_PATIENTS_GUARD_PAGE_SIZE = 200

const defaultSearchParams: PhysicianDetailRequestDto = {
  sortProgram: [
    {
      sortBy: SortDirectionType.Ascending.toUpperCase(),
      name: programColumns[0].name
    }
  ]
}

interface IPhysicianDetailsRouteParams {
  physicianId: string
}
interface IPhysicianDetailsProps
  extends RouteComponentProps<IPhysicianDetailsRouteParams> {}

export const PhysicianDetailsContainer: FunctionComponent<
  IPhysicianDetailsProps
> = ({ history, location, match }) => {
  const { physicianId } = match.params

  const { t } = useTranslation()
  const { dispatch, institute, portfolioCountryCode } = useAppContext()
  const selectedInstituteId = institute.data?.instituteId
  // Restore page state from URL params
  const urlSearchParams = new URLSearchParams(location.search)

  const [physician, setPhysician] = React.useState<PhysicianDetailsDto>()
  const [isRemovalAllowed, setIsRemovalAllowed] = React.useState<
    boolean | undefined
  >(undefined)
  // TabIndex
  const [selectedTabIndex, setSelectedTabIndex] = React.useState<number>(() => {
    const tabIndex = urlSearchParams.get('tabIndex')
    let defaultTabIndex: number = 0
    if (typeof tabIndex === 'string') {
      defaultTabIndex = Number.parseInt(tabIndex)
    }
    return defaultTabIndex
  })
  const [isPhysicianLoading, setIsPhysicianLoading] = React.useState(false)
  const [isPatientsLoading, setIsPatientsLoading] = React.useState(false)
  const [isDiscontinued, setIsDiscontinued] = React.useState(false)
  const [searchParams, setSearchParams] = React.useState(defaultSearchParams)
  const [patientStatusFilter, setPatientStatusFilter] = React.useState<
    FacetDto[] | undefined
  >()

  // Sorting column
  const [sortColumn, setSortColumn] = React.useState<ProgramColumn>(() => {
    const defaultSortColumn = programColumns[0] // Default is ProgramName
    const restoredSortByColumn = getSortColumnFromURLParams(
      urlSearchParams,
      defaultSortColumn
    )
    defaultSearchParams.sortProgram[0].name = restoredSortByColumn
      ? restoredSortByColumn.name.toString()
      : defaultSortColumn.name.toString()
    return restoredSortByColumn
  })
  // Sorting direction
  const [sortDirection, setSortDirection] = React.useState<SortDirectionType>(
    () => {
      const sortDirection = urlSearchParams.get('sortDirection')
      const restoredSortDirection = $enum(SortDirectionType).asValueOrDefault(
        sortDirection,
        SortDirectionType.Ascending
      )
      defaultSearchParams.sortProgram[0].sortBy = restoredSortDirection
        .toString()
        .toUpperCase()
      return restoredSortDirection
    }
  )

  const [isTransferOrDiscontinueOpen, setIsTransferOrDiscontinueOpen] =
    React.useState<boolean>(false)

  const [dissociateModalMode, setDissociateModalMode] = React.useState<
    ModalMode | undefined
  >()
  const [dissociateError, setDissociateError] = React.useState<string>()

  const [discontinueModalMode, setDiscontinueModalMode] = React.useState<
    ModalMode | undefined
  >()
  const [discontinueError, setDiscontinueError] = React.useState<string>()

  const handlePatientsError = useErrorMessage(
    'There was an error fetching patients.'
  )
  const handlePhysicianDetailsError = useErrorMessage(
    'There was an error fetching the physicians details.'
  )

  const handleGoBack = () => {
    history.push('/programs/my-physicians')
  }

  const handleProgramRowClicked = (programId: number) => {
    history.push(`/programs/access-programs/${programId}`)
  }

  const toggleSortDirection = (
    current: SortDirectionType
  ): SortDirectionType => {
    if (current === SortDirectionType.None) {
      return SortDirectionType.Descending
    }
    if (current === SortDirectionType.Descending) {
      return SortDirectionType.Ascending
    }
    return SortDirectionType.None
  }

  const handleToggleSort = (columnName: ProgramColumn) => {
    setIsPhysicianLoading(true)
    const newSortDirection =
      sortColumn.viewName === columnName.viewName
        ? toggleSortDirection(sortDirection)
        : SortDirectionType.Descending
    setSortDirection(newSortDirection)
    setSortColumn(columnName)
    setSearchParams({
      ...searchParams,
      sortProgram: [
        {
          sortBy: newSortDirection.toUpperCase(),
          name: columnName.name
        }
      ]
    })
  }

  useEffect(() => {
    setIsRemovalAllowed(undefined)

    if (!selectedInstituteId) {
      setIsPatientsLoading(false)
      return
    }

    setIsPatientsLoading(true)
    const cancelSource = axios.CancelToken.source()
    let isMounted = true

    resolveDiscontinueAvailability(
      (skip) =>
        getPatients(
          {
            query: '',
            filter: {
              programs: [],
              patientOrderStatuses: [],
              physicianId: Number(physicianId)
            },
            pagination: { skip, take: PHYSICIAN_PATIENTS_GUARD_PAGE_SIZE },
            sorting: {
              sortBy: sortOptions[0].optionId,
              order: SortDirectionType.Ascending.toUpperCase()
            }
          },
          cancelSource.token
        ).then((response) =>
          response
            ? {
                result: response.data.result,
                total: response.data.pagination.total
              }
            : null
        ),
      PHYSICIAN_PATIENTS_GUARD_PAGE_SIZE
    )
      .then((removalAllowed) => {
        if (!isMounted) return
        setIsPatientsLoading(false)
        setIsRemovalAllowed(removalAllowed)
      })
      .catch((error: AxiosError) => {
        if (!isMounted || axios.isCancel(error)) return
        handlePatientsError(error)
        setIsRemovalAllowed(false)
        setIsPatientsLoading(false)
      })

    return () => {
      isMounted = false
      cancelSource.cancel()
    }
  }, [handlePatientsError, physicianId, selectedInstituteId])

  useEffect(() => {
    getPhysicianById(physicianId, searchParams)
      .then((response) => {
        setIsPhysicianLoading(false)
        if (response) {
          setPhysician(response.data)
        }
      })
      .catch((error: AxiosError) => {
        handlePhysicianDetailsError(error)
        setIsPhysicianLoading(false)
      })

    return () => {
      cancelGetPhysicianById()
    }
  }, [dispatch, handlePhysicianDetailsError, physicianId, searchParams])

  const handleRequestToDissociate = () => {
    setDissociateModalMode(ModalMode.active)
  }

  // handlers of 'Transfer or discontinue all patients associated with physician' modal window
  const handleViewPatients = () => {
    setSelectedTabIndex(0)
    setPatientStatusFilter([
      { value: AssociatedPatientsStatus.ActiveTreatment, count: 1 }
    ])
    setIsTransferOrDiscontinueOpen(false)
  }

  const handleTransferOrDiscontinueClose = () => {
    setIsTransferOrDiscontinueOpen(false)
  }

  // handlers of disassociate modal window
  const handleDissociateConfirmation = () => {
    setDissociateModalMode(ModalMode.submitting)
    physicianId &&
      disassociatePhysician(physicianId)
        .then((response) => {
          if (response.status === 200) {
            handleGoBack()
            setTimeout(() => {
              dispatch(
                createAnnounceEvent(
                  AnnounceMode.Success,
                  `${physician?.physicianTitle ?? ''} ${
                    physician?.physicianFirstName
                  } ${
                    physician?.physicianLastName
                  } has been removed from your physician list.`,
                  'Complete'
                )
              )
            }, 100)
            analyticsServiceSingleton.trackEvent(
              AnalyticsEvent.DisassociatePhysician,
              { outcome: 'Succeeded' }
            )
          }
        })
        .catch((error: AxiosError) => {
          setDissociateModalMode(ModalMode.hasError)
          setDissociateError(
            getDependencyErrorMessage(error) ??
              t('physician_details:removal_error_fallback')
          )
        })
  }

  const handleDissociateClose = () => {
    setDissociateModalMode(undefined)
  }

  // handlers of discontinue modal window

  const handleRequestToRemove = () => {
    setDiscontinueModalMode(ModalMode.active)
  }

  const handleDiscontinueConfirmation = () => {
    if (isRemovalAllowed === true) {
      setDiscontinueModalMode(ModalMode.submitting)
      physicianId &&
        discontinuePhysician(physicianId)
          .then((response) => {
            if (response.status === 200) {
              setDiscontinueModalMode(undefined)
              setIsDiscontinued(true)
              analyticsServiceSingleton.trackEvent(
                AnalyticsEvent.RemovePhysician,
                {
                  physicianId: physician?.physicianId,
                  instituteId: institute.data?.instituteId
                }
              )
            }
          })
          .catch((error: AxiosError) => {
            setDiscontinueModalMode(ModalMode.hasError)
            setDiscontinueError(
              getDependencyErrorMessage(error) ??
                t('physician_details:removal_error_fallback')
            )
          })
    } else {
      setDiscontinueModalMode(undefined)
      setIsTransferOrDiscontinueOpen(true)
    }
  }

  const handleDiscontinueClose = () => {
    setDiscontinueModalMode(undefined)
  }

  useUpdateQueryParams([
    { sortColumn: sortColumn.name.toString() },
    { sortDirection: sortDirection.toString() },
    { tabIndex: selectedTabIndex }
  ])

  const physicianFullName = `${physician?.physicianTitle ?? ''} ${
    physician?.physicianFirstName
  } ${physician?.physicianLastName}`

  type Deps = [boolean, PhysicianDetailsDto, IRestEndpointState<InstituteDto>]
  useEffectOnlyOnce(
    (dependencies: Deps) => {
      analyticsServiceSingleton.trackPageView(
        AnalyticsPageEvent.ViewPhysician,
        {
          'Physician ID': dependencies[1].physicianId,
          'Institute ID': dependencies[2].data?.instituteId
        }
      )
    },
    [isPatientsLoading && isPhysicianLoading, physician, institute],
    (dependencies: Deps) =>
      dependencies[0] === false && dependencies[1] && dependencies[2]
  )

  return (
    <>
      {physician && (
        <>
          <DissociateModal
            isOpen={dissociateModalMode !== undefined}
            isSubmitting={dissociateModalMode === ModalMode.submitting}
            hasError={dissociateModalMode === ModalMode.hasError}
            errorMessage={dissociateError}
            physicianName={physicianFullName}
            handleConfirmation={handleDissociateConfirmation}
            handleClose={handleDissociateClose}
          />
          <DiscontinueModal
            isOpen={discontinueModalMode !== undefined}
            isSubmitting={discontinueModalMode === ModalMode.submitting}
            hasError={discontinueModalMode === ModalMode.hasError}
            errorMessage={discontinueError}
            handleConfirmation={handleDiscontinueConfirmation}
            handleClose={handleDiscontinueClose}
          />
          <TransferOrDiscontinueModal
            isOpen={isTransferOrDiscontinueOpen}
            physicianName={physicianFullName}
            handleConfirmation={handleViewPatients}
            handleClose={handleTransferOrDiscontinueClose}
          />
        </>
      )}
      <PhysicianDetails
        isLoading={isPatientsLoading && isPhysicianLoading}
        isDiscontinued={isDiscontinued}
        selectedTabIndex={selectedTabIndex}
        onTabSelected={setSelectedTabIndex}
        physician={physician}
        selectedSortColumn={sortColumn}
        selectedSortDirection={sortDirection}
        columns={programColumns}
        userCountry={portfolioCountryCode}
        handleToggleSort={handleToggleSort}
        handleGoBack={handleGoBack}
        handleRequestToDissociate={handleRequestToDissociate}
        handleRequestToRemove={handleRequestToRemove}
        handleProgramRowClicked={handleProgramRowClicked}
        renderAssociatedPatients={({ physicianId }) => (
          <AssociatedPatientsContainer
            physicianId={physicianId}
            preselectedPatientStatusFilters={patientStatusFilter}
          />
        )}
      />
    </>
  )
}
